import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { SectionHeader, CTABanner } from "@/components/Shared";
import { IMAGES, DIRECTORS } from "@/data/content";

const BELIEFS = [
  { title: "Strategic Location", description: "We believe great development begins with locations that offer connectivity, context and future potential." },
  { title: "Thoughtful Planning", description: "Every opportunity deserves careful evaluation, layout thinking and a clear execution strategy." },
  { title: "Quality Execution", description: "We hold every stage of development to a standard of quality and usability." },
  { title: "Transparency", description: "Clear communication and responsible business practices remain central to how we work." },
  { title: "Long-Term Value", description: "We measure success by the lasting value our developments create for people and communities." },
];

const About = () => (
  <>
    <SEO
      title="About Evaya Ventures | Emerging Real Estate Development Company"
      description="Evaya Ventures is an emerging real-estate development company focused on identifying opportunities, creating thoughtful development strategies and delivering long-term value."
      path="/about"
      breadcrumbs={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]}
    />

    <PageHero
      overline="About Evaya Ventures"
      title="Creating Opportunities. Shaping Possibilities."
      description="Evaya Ventures is an emerging real-estate development company focused on creating meaningful opportunities through redevelopment and plotted land development."
      image={IMAGES.meeting}
      imageAlt={IMAGES.meetingAlt}
      testId="about-hero"
    />

    {/* Philosophy */}
    <section className="bg-white py-24 lg:py-32" data-testid="about-philosophy">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <SectionHeader
          overline="What We Believe"
          title="Real estate development should combine five things"
          testId="about-beliefs"
        />
        <div className="mt-14 grid grid-cols-1 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-5">
          {BELIEFS.map((belief, i) => (
            <div key={belief.title} className="bg-white">
              <Reveal delay={i * 0.06} className="h-full p-8">
                <span className="font-display text-4xl font-light text-brand">{belief.num || `0${i + 1}`}</span>
                <h3 className="mt-4 font-display text-2xl text-ink">{belief.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">{belief.description}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Brand story */}
    <section className="border-t border-ink/10 bg-sand py-24 lg:py-32" data-testid="about-story">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-6">
          <SectionHeader
            overline="Where We Are"
            title="At the beginning of a deliberate journey"
            testId="about-story"
          />
          <Reveal delay={0.1}>
            <p className="mt-6 text-base leading-relaxed text-ink/65 md:text-lg">
              Our approach combines careful opportunity identification, thoughtful planning, responsible execution and a long-term perspective on real estate.
            </p>
            <p className="mt-5 text-base leading-relaxed text-ink/65">
              We believe successful development is not simply about creating buildings or parcels of land. It is about understanding locations, people, possibilities and the value that thoughtful development can create over time.
            </p>
            <p className="mt-5 text-base leading-relaxed text-ink/65">
              Evaya Ventures is at the start of its development journey. Rather than claim a history we do not have, we are focused on building the company the right way — with disciplined opportunity evaluation, transparent communication and a development philosophy designed to last.
            </p>
            <p className="mt-7 font-display text-xl italic text-brand-dark md:text-2xl" data-testid="about-tagline">
              From Earth to Extraordinary.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-6">
          <div className="relative">
            <div className="absolute -right-4 -top-4 h-full w-full border border-brand/40" aria-hidden="true" />
            <img src={IMAGES.strategy} alt={IMAGES.strategyAlt} loading="lazy" className="relative h-[400px] w-full object-cover" />
          </div>
        </Reveal>
      </div>
    </section>

    {/* Leadership */}
    <section className="bg-white py-24 lg:py-32" data-testid="about-leadership">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <SectionHeader
          overline="Leadership"
          title="Guided by integrity and long-term thinking"
          description="Evaya Ventures is driven by a leadership team committed to building the company on the foundations of integrity, responsible development and long-term value creation."
          testId="about-leadership"
        />
        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
          {DIRECTORS.map((director, i) => (
            <Reveal key={director.name} delay={i * 0.1}>
              <article
                data-testid={`director-card-${i + 1}`}
                className="flex h-full flex-col overflow-hidden border border-ink/10 border-t-2 border-t-brand bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow duration-500 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
              >
                <img
                  src={director.photo}
                  alt={`${director.name}, ${director.role}, Evaya Ventures`}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover object-top"
                  data-testid={`director-photo-${i + 1}`}
                />
                <div className="flex flex-1 flex-col p-8 lg:p-10">
                  <h3 className="font-display text-3xl text-ink" data-testid={`director-name-${i + 1}`}>{director.name}</h3>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-[0.24em] text-brand-dark">{director.role}</p>
                  <p className="mt-5 flex-1 text-base leading-relaxed text-ink/65">{director.bio}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CTABanner title="Connect with Evaya Ventures" copy="We welcome conversations with landowners, housing societies, channel partners and investors who share our long-term view." />
  </>
);

export default About;
